import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Label, Icon } from 'semantic-ui-react';
import Avatar from './avatar';
import { useUserContext } from '~/hooks';
import PopupMenuTrigger from './popups/PopupMenuTrigger';
import { SidebarProvider } from './context';
import { useNotificationCount } from '~/requests/notifications';
import { storage } from '~/utils';
import sendMenuClickEvent from '~/utils/analytics/sendMenuClickEvent';
import './styles.scss';
import { DevAppEnvironments } from '~/constants/app-environments';
import useSendNotificationBatch from '~/mutations/admin/useSendNotificationBatch';
import { Loader } from 'semantic-ui-react';

export const MainNav = () => {
  const { loggedInUser } = useUserContext();
  const { data: notificationCount } = useNotificationCount();
  const [utilityNavOpen, setUtilityNavOpen] = useState<boolean>(false);
  const [devActionDropdownOpen, setDevActionDropdownOpen] = useState<boolean>(false);
  const [adminNavOpen, setAdminNavOpen] = useState<boolean>(false);
  const utilityDropdownRef = useRef<HTMLDivElement>(null);
  const devDropdownRef = useRef<HTMLDivElement>(null);
  const adminNavRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const lastScrollY = useRef<number>(0); // Ref to track the last scroll position
  const SCROLL_Y_TRIGGER = 600;
  const [scrollTopClasses, setScrollTopClasses] = useState<string>('scroll-top-button hidden');
  const appEnvironment = window.location.hostname.split('.')[0];
  const { mutateAsync: sendEmailNotifications, status: sendEmailNotificationsStatus } = useSendNotificationBatch();
  const [showMobileNav, setShowMobileNav] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > SCROLL_Y_TRIGGER) {
        setScrollTopClasses('scroll-top-button');
      } else {
        setScrollTopClasses('scroll-top-button hidden');
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);


  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setUtilityNavOpen(false);
    setDevActionDropdownOpen(false);
    setAdminNavOpen(false);
    setShowMobileNav(false);
  }, [location]);

  const handleClickOutside = (event: MouseEvent) => {
    if (utilityDropdownRef.current && !utilityDropdownRef.current.contains(event.target as Node)) {
      setUtilityNavOpen(false);
    }
    if (devDropdownRef.current && !devDropdownRef.current.contains(event.target as Node)) {
      setDevActionDropdownOpen(false);
    }
    if (adminNavRef.current && !adminNavRef.current.contains(event.target as Node)) {
      setAdminNavOpen(false);
    }
  };

  return (

    <>
      <Button secondary className={`${scrollTopClasses}`} onClick={() => window.scrollTo({ top: 0 })}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-up-short" viewBox="0 0 16 16" style={{ width: '22px', height: '22px' }}>
          <path fillRule="evenodd" d="M8 12a.5.5 0 0 0 .5-.5V5.707l2.146 2.147a.5.5 0 0 0 .708-.708l-3-3a.5.5 0 0 0-.708 0l-3 3a.5.5 0 1 0 .708.708L7.5 5.707V11.5a.5.5 0 0 0 .5.5" />
        </svg>
        <span>Back to top</span>
      </Button>

      <div className="main-nav-container">

        <div className="main-nav">
          <div className="main-nav-left">

            <Button
              className="main-nav-toggle"
              onClick={() => setShowMobileNav((prev) => !prev)}
              aria-label="Toggle navigation"
            >
              <Icon name={showMobileNav ? 'close' : 'sidebar'} size="large" />
            </Button>

            <a href="/" className="logo" onClick={() => setShowMobileNav(false)}>
              <img src="/logo-longform.png" alt="Conductor Solar Home" />
            </a>
            <nav className={'main-nav-links ' + (showMobileNav ? 'show' : '')}>

              {(!loggedInUser?.isInstaller && !loggedInUser?.isExternalCompany && !loggedInUser?.isAdmin) ? (
                <Link to="/dashboard" onClick={() => setShowMobileNav(false)} className="nav-link">
                  Dashboard
                </Link>
              ) : null}

              <Link to="/my-deals" onClick={() => setShowMobileNav(false)} className="nav-link">
                My Deals
              </Link>

              {!loggedInUser?.isInstaller && (
                <Link
                  to="/listings"
                  onClick={() => setShowMobileNav(false)}
                  className="nav-link"
                >
                  Listings
                </Link>
              )}

              {loggedInUser?.isInstaller ? (
                <>
                  {(!loggedInUser?.isInvestor) ? (
                    <Link to="/buyer-network" onClick={() => sendMenuClickEvent('Buyer Network')} className="nav-link">
                      Buyer Network
                    </Link>
                  ) : null}
                </>
              ) : null}

              {loggedInUser?.isAdmin ? (
                <>
                  <div className="dropdown nav-dropdown" ref={adminNavRef}>
                    <button
                      className="nav-link nav-dropdown-toggle"
                      onClick={() => {
                        setAdminNavOpen((prev) => !prev);
                      }}
                    >
                      Admin
                    </button>
                    {adminNavOpen && (
                      <div className="dropdown-menu nav-dropdown-menu">
                        <Link to="/bids" onClick={() => setShowMobileNav(false)} className="dropdown-item">
                          Bids
                        </Link>
                        <Link to="/admin/user-activity" onClick={() => setShowMobileNav(false)} className="dropdown-item">
                          User Activity
                        </Link>
                        <Link to="/admin/ingestion-log" onClick={() => setShowMobileNav(false)} className="dropdown-item">
                          Ingestion Log
                        </Link>
                        <Link to="/admin/notifications" onClick={() => setShowMobileNav(false)} className="dropdown-item">
                          Notification Log
                        </Link>
                        <Link to="/admin/mcp-access" onClick={() => setShowMobileNav(false)} className="dropdown-item">
                          Claude MCP Access
                        </Link>
                        <Link to="/users" onClick={() => setShowMobileNav(false)} className="dropdown-item">
                          Users
                        </Link>
                        <Link to="/companies" onClick={() => setShowMobileNav(false)} className="dropdown-item">
                          Companies
                        </Link>
                        <Link
                          to="/buyer-network"
                          onClick={() => {
                            sendMenuClickEvent('Buyer Network');
                            setShowMobileNav(false);
                          }}
                          className="dropdown-item"
                        >
                          Buyer Network
                        </Link>
                        <Link to="/dashboard" onClick={() => setShowMobileNav(false)} className="dropdown-item">
                          Reports
                        </Link>
                      </div>
                    )}
                  </div>
                </>
              ) : null}

              {/* 
                <PopupMenuTrigger name="projects">
                  <span>Projects</span>
                </PopupMenuTrigger>*/}
              {/* Portfolios nav item removed for admins */}
            </nav>
          </div>
          <div className="main-nav-right">
            {DevAppEnvironments.includes(appEnvironment) &&    // Never show for prod
              <div id="env-tag" className={loggedInUser.isAdmin ? 'dropdown' : undefined}>
                <span className={loggedInUser.isAdmin ? 'dropdown-toggle' : undefined}>
                  <Label
                    size="large"
                    style={{
                      backgroundColor: '#267907',
                      color: 'var(--color-pure-white)',
                      padding: '2px 4px',
                      fontWeight: 'bold',
                      fontSize: '12px',
                    }}
                    onClick={() => setDevActionDropdownOpen((prev) => !prev)}
                  >
                    {appEnvironment.toUpperCase()}
                  </Label>
                </span>
                {devActionDropdownOpen && loggedInUser.isAdmin && (
                  // Add any other admin testing environemnt actions here in the future
                  <div ref={devDropdownRef} className="dropdown-menu" style={{ width: 'fit-content', right: '7px' }}>
                    {(sendEmailNotificationsStatus === 'loading') ?
                      <div className='dropdown-item' style={{ minHeight: '40px' }}>
                        <Loader active size='small' />
                      </div> :
                      <span className='dropdown-item' onClick={async () => {
                        await sendEmailNotifications();
                        setDevActionDropdownOpen(false);
                      }}>
                        Send email batch
                      </span>}
                  </div>
                )}
              </div>
            }
            <SidebarProvider>
              <PopupMenuTrigger name="notifications">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-bell-fill" viewBox="0 0 16 16">
                  <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2m.995-14.901a1 1 0 1 0-1.99 0A5 5 0 0 0 3 6c0 1.098-.5 6-2 7h14c-1.5-1-2-5.902-2-7 0-2.42-1.72-4.44-4.005-4.901" />
                </svg>
                {notificationCount > 0 && (
                  <span className="notification-count">
                    {notificationCount}
                  </span>
                )}
              </PopupMenuTrigger>
            </SidebarProvider>

            <div className="dropdown">
              <button
                className="dropdown-toggle"
                onClick={() => setUtilityNavOpen((prev) => !prev)}
              >
                <div className="user-info">
                  <span className="user-name">{loggedInUser?.name}</span>
                  <span className="user-role">
                    {storage.getSpoofedEmail() && <span className="spoofed">(Spoofed)</span>}
                    {loggedInUser?.role?.code === 'INSTALLER' ? 'DEVELOPER' : loggedInUser?.role?.code}
                  </span>
                </div>
                <Avatar />
              </button>
              {utilityNavOpen && (
                <div ref={utilityDropdownRef} className="dropdown-menu">
                  <Link to="/settings" onClick={() => sendMenuClickEvent('Settings')} className="dropdown-item">
                    Settings
                  </Link>
                  <a href="https://conductor.solar/blog/how-to-use-conductor" target="_blank" rel="noopener noreferrer" onClick={() => sendMenuClickEvent('Blog')} className="dropdown-item">
                    How to use Conductor
                  </a>
                  {!loggedInUser?.isAdmin && (
                    <a href={'mailto:' + (loggedInUser.companyOwner?.email ?? 'support@conductor.solar')} onClick={() => sendMenuClickEvent('Contact Us')} className="dropdown-item">
                      Contact Us
                    </a>
                  )}
                  <a href="https://conductor.solar/company-blog" target="_blank" rel="noopener noreferrer" onClick={() => sendMenuClickEvent('Blog')} className="dropdown-item">
                    Blog
                  </a>
                  <Link to="/logout" className="dropdown-item">
                    Log out
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};